import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getAvailablePlans, choosePlan } from '../../services/api';
import { formatMoneyBRL } from '../../components/MoneyInput';

/**
 * Página "Escolher plano" — self-serve pós-trial.
 *
 * Tenant vê os planos disponíveis com toggle mensal/anual,
 * seleciona e clica "Contratar". Backend gera contrato + fatura + PIX.
 * Redireciona pra /app/billing onde vê o QR pra pagar.
 */
export default function ChoosePlanPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [choosing, setChoosing] = useState(null);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    getAvailablePlans()
      .then(r => {
        setPlans(r.data.plans || []);
        setCampaign(r.data.active_campaign || null);
      })
      .catch(() => toast.error('Erro ao carregar planos'))
      .finally(() => setLoading(false));
  }, []);

  const handleChoose = async (plan) => {
    setChoosing(plan.id);
    try {
      const r = await choosePlan({ plan_id: plan.id, billing_period: period });
      toast.success(r.data.message || 'Plano contratado!');
      // Se tem PIX, redireciona pro billing pra pagar
      navigate('/app/billing', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao contratar plano');
    } finally {
      setChoosing(null);
    }
  };

  const getPrice = (plan) => {
    const monthly = plan.prices?.monthly?.amount ?? parseFloat(plan.price) ?? 0;
    const annual = plan.prices?.annual?.amount ?? (monthly * 10);
    if (period === 'annual') {
      const monthlyEquiv = annual / 12;
      return { display: monthlyEquiv, total: annual, original: monthly, saving: (monthly * 12) - annual };
    }
    return { display: monthly, total: monthly, original: null, saving: 0 };
  };

  if (loading) return <div className="page-body"><div className="spinner" style={{ margin: '60px auto', display: 'block' }} /></div>;

  return (
    <div className="page-body">
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--gray-900)', margin: '0 0 8px' }}>
          Escolha seu plano
        </h2>
        <p style={{ color: 'var(--gray-600)', fontSize: 15, margin: '0 0 20px' }}>
          Selecione o plano ideal pro seu negócio. Você pode trocar a qualquer momento.
        </p>

        {/* Toggle mensal/anual */}
        <div style={{
          display: 'inline-flex', gap: 0, background: 'var(--gray-100)',
          borderRadius: 10, padding: 4,
        }}>
          <button
            onClick={() => setPeriod('monthly')}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: 'none', borderRadius: 8, fontFamily: 'inherit',
              background: period === 'monthly' ? '#fff' : 'transparent',
              color: period === 'monthly' ? 'var(--gray-900)' : 'var(--gray-500)',
              boxShadow: period === 'monthly' ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
            }}>Mensal</button>
          <button
            onClick={() => setPeriod('annual')}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: 'none', borderRadius: 8, fontFamily: 'inherit',
              background: period === 'annual' ? 'var(--primary)' : 'transparent',
              color: period === 'annual' ? '#fff' : 'var(--gray-500)',
            }}>
            Anual <span style={{ fontSize: 11, opacity: 0.85 }}>(2 meses grátis)</span>
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 16,
        maxWidth: 1000,
        margin: '0 auto',
      }}>
        {plans.map(plan => {
          const p = getPrice(plan);
          const features = typeof plan.features === 'string' ? JSON.parse(plan.features || '{}') : (plan.features || {});
          const featureList = Object.entries(features).filter(([, v]) => v === true).map(([k]) => k);

          return (
            <div key={plan.id} style={{
              background: 'var(--white)', border: '2px solid var(--gray-200)',
              borderRadius: 14, padding: '24px 20px', display: 'flex',
              flexDirection: 'column', position: 'relative',
              ...(plan.name === 'Essencial' ? { borderColor: 'var(--primary)', boxShadow: '0 4px 20px rgba(37,99,235,.12)' } : {}),
            }}>
              {plan.name === 'Essencial' && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: 'var(--primary)', color: '#fff', padding: '3px 14px',
                  borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>Mais escolhido</div>
              )}

              <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px', color: 'var(--gray-900)' }}>
                {plan.name}
              </h3>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '0 0 16px' }}>
                {plan.name === 'Starter' && 'Profissional autônomo'}
                {plan.name === 'Essencial' && 'Pequeno negócio'}
                {plan.name === 'Profissional' && 'Equipe em crescimento'}
                {plan.name === 'Enterprise' && 'Operação completa'}
              </p>

              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 14, color: 'var(--gray-500)' }}>R$</span>
                <span style={{ fontSize: 36, fontWeight: 800, color: 'var(--gray-900)', letterSpacing: -1 }}>
                  {p.display.toFixed(2).split('.')[0]}
                </span>
                <span style={{ fontSize: 14, color: 'var(--gray-500)' }}>
                  ,{p.display.toFixed(2).split('.')[1]}/mês
                </span>
                {period === 'annual' && (
                  <div style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600, marginTop: 4 }}>
                    {formatMoneyBRL(p.total)} ao ano · Economia de {formatMoneyBRL(p.saving)}
                  </div>
                )}
              </div>

              <div style={{ flex: 1, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 6, fontWeight: 600 }}>Inclui:</div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 13, color: 'var(--gray-700)' }}>
                  {plan.max_users > 0 && <li style={{ marginBottom: 4 }}>✓ Até <strong>{plan.max_users}</strong> usuários</li>}
                  {plan.max_users === 0 && <li style={{ marginBottom: 4 }}>✓ Usuários <strong>ilimitados</strong></li>}
                  {featureList.slice(0, 6).map(f => (
                    <li key={f} style={{ marginBottom: 4 }}>✓ {
                      f === 'google_calendar' ? 'Google Calendar' :
                      f === 'whatsapp' ? 'WhatsApp automático' :
                      f === 'waitlist' ? 'Lista de espera' :
                      f === 'nps' ? 'Pesquisa NPS' :
                      f === 'intake_forms' ? 'Formulários' :
                      f === 'deposits' ? 'Sinal via PIX' :
                      f === 'loyalty' ? 'Fidelidade' :
                      f === 'packages' ? 'Pacotes' :
                      f === 'api_access' ? 'API pública' :
                      f === 'multi_unit' ? 'Multi-unidade' :
                      f === 'custom_domain' ? 'Domínio próprio' :
                      f.replace(/_/g, ' ')
                    }</li>
                  ))}
                  {featureList.length > 6 && (
                    <li style={{ color: 'var(--gray-400)', fontSize: 12 }}>+ {featureList.length - 6} mais</li>
                  )}
                </ul>
              </div>

              <button
                onClick={() => handleChoose(plan)}
                disabled={choosing !== null}
                className={`btn ${plan.name === 'Essencial' ? 'btn-primary' : 'btn-secondary'}${choosing === plan.id ? ' btn-loading' : ''}`}
                style={{ width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 700 }}
              >
                {choosing === plan.id ? '' : `Contratar ${plan.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--gray-400)' }}>
        Você pode trocar de plano ou cancelar a qualquer momento em Configurações.
      </div>
    </div>
  );
}
